import { OddsButton } from '@/components/widgets/odds-button';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { useTranslations } from 'next-intl';
import { useAccountInfo } from '@/hooks/query/account';
import { BetTypeContentProps } from '../bet-type-content';
import { addCommas, getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { BetChoices } from '@/types/bet-type-settings';
import { Fragment, useState } from 'react';
import { QuestionIcon } from '@/components/icons/svg-icons';
import { Button } from '@nf/ui';
import { Market } from '@nf/types/odds';
import { useGetBetTypeSetting } from '@/hooks';

export const BetTypePenalty = ({ matchModel, betTypeArray }: BetTypeContentProps) => {
	const getBetTypeSetting = useGetBetTypeSetting();
	const [isHelpOpen, setIsHelpOpen] = useState(false);
	const [homeChoice, setHomeChoice] = useState('-----');
	const [awayChoice, setAwayChoice] = useState('-----');

	const t = useTranslations('General');

	const { user } = useAccountInfo();
	const oddsData = useAtomValue(oddsPageAtom);

	const market = betTypeArray[0];
	const setting = getBetTypeSetting(market.BetTypeId, matchModel.GameID);
	const { betChoices } = setting;
	const betTeamArray = [...new Set(betChoices.map((choice) => choice.betTeam))];

	const handleHelpOpen = () => {
		setIsHelpOpen(!isHelpOpen);
	}

	const handleReset = () => {
		setHomeChoice('-----');
		setAwayChoice('-----');
	}

	const getPrice = () => {
		const price: Record<string, number> = {};
		if (homeChoice === '-----' && awayChoice === '-----') {
			return 0;
		}
		Object.entries(market.Selections).forEach(([key, selection]) => {
			price[key] = selection.Price;
		});
		const finalPrice = (homeChoice + awayChoice).split('').reduce((result, choice, index) => {
			const team = index < 5 ? 'h' : 'a';
			return !result ? (price[team + choice] || 0) : result * (price[team + choice] || 1);
		}, 0);
		return fixedDecimal(Math.round(finalPrice * 100) / 100, 2);
	}

	const fixedDecimal = (value: number, num: number) => {
		const arr = value.toString().split('.');
		let dec = (arr[1] || '').substring(0, num);
		while (dec.length < num) {
			dec += '0';
		}
		return parseFloat(addCommas(arr[0] + '.' + dec).replace(/,/g, ''));
	};

	return (
		<div className="bettype__content bettype__content--penalty">
			<div className="bettype__question" onClick={handleHelpOpen}>
				<Button variant="icon">
					<QuestionIcon />
				</Button>
				<div className="tip tip--detail tip--right" data-open={isHelpOpen}>
					<div className="scrollbar scrollbar--tip">
						<div className="tip__content">
							{t('lbl_PSCBF10Hint')}
						</div>
					</div>
				</div>
			</div>
			<div className="bettype__col">
				<div className="bettype__title">{getHomeTeamName(matchModel?.TeamId1, matchModel?.Neu, t('Neutral_Team'), oddsData?.teamNameMap)}</div>
				<div className="bettype__group">
					<PenaltyOptionPanel
						market={market}
						betTeamArray={betTeamArray}
						betChoices={betChoices}
						betTeamType="h"
						homeChoice={homeChoice}
						setHomeChoice={setHomeChoice}
						awayChoice={awayChoice}
						setAwayChoice={setAwayChoice}
					/>
				</div>
				<div className="bettype__title">{getTeamName(matchModel?.TeamId2, oddsData?.teamNameMap)}</div>
				<div className="bettype__group">
					<PenaltyOptionPanel
						market={market}
						betTeamArray={betTeamArray}
						betChoices={betChoices}
						betTeamType="a"
						homeChoice={homeChoice}
						setHomeChoice={setHomeChoice}
						awayChoice={awayChoice}
						setAwayChoice={setAwayChoice}
					/>
				</div>
			</div>
			<div className="bettype__col bettype__col--odds">
				<OddsButton
					key={`${market.Line}_${homeChoice}_${awayChoice}`}
					match={matchModel}
					market={market}
					selection={market.Selections[betChoices[0].betTeam]}
					homeTeamName={''}
					awayTeamName={''}
					variant="default"
					betTeam={`${homeChoice}${awayChoice}`}
					originalPrice={getPrice()}
					sport={matchModel?.GameID}
					type="OU"
					oddsType={user?.OType}
					accountStatus={user?.ActStatus ?? 0}
				/>
				<button className="btn" onClick={handleReset} data-disabled={getPrice() ? false : true}>
					<span className="btn__text">{t('lbl_Reset')}</span>
				</button>
			</div>
		</div>
	)
};

const PenaltyOptionPanel = ({
	market,
	betTeamArray,
	betChoices,
	betTeamType,
	homeChoice,
	setHomeChoice,
	awayChoice,
	setAwayChoice
}: {
	market: Market,
	betTeamArray: string[],
	betChoices: BetChoices[],
	betTeamType: string,
	homeChoice: string,
	setHomeChoice: (value: string) => void,
	awayChoice: string,
	setAwayChoice: (value: string) => void
}) => {
	const t = useTranslations('General');
	const { IsCP, Line } = market;

	const handleBallClick = (betTeam: string, index: number) => {
		if (betTeam.startsWith('h')) {
			setHomeChoice(combineChoice(homeChoice, betTeam, index));
		} else if (betTeam.startsWith('a')) {
			setAwayChoice(combineChoice(awayChoice, betTeam, index));
		} else {
			setHomeChoice(combineChoice('', '-', index));
			setAwayChoice(combineChoice('', '-', index));
		}
	}

	const combineChoice = (teamChoice: string, betTeam: string, index: number) => {
		if (betTeam === '-') {
			return '-----';
		}
		const choice = teamChoice[index] === betTeam.slice(-1) ? '-' : betTeam.slice(-1);
		return `${teamChoice.substring(0, index)}${choice}${teamChoice.substring(index + 1)}`;
	}

	const chkSelected = (betTeam: string, index: number) => {
		if (betTeam.startsWith('h')) {
			return homeChoice[index] === betTeam.slice(-1);
		} else if (betTeam.startsWith('a')) {
			return awayChoice[index] === betTeam.slice(-1);
		}
		return false;
	}

	return (
		betTeamArray.filter((betTeam) => betTeam.startsWith(betTeamType)).map((betTeam) => {
			return (
				<Fragment key={betTeam}>
					<div className="bettype__text">
						{betTeam.endsWith('s') ? t('lbl_Score') : t('lbl_Miss')}
					</div>
					{
						betChoices.filter((choice) => choice.betTeam === betTeam).map((choice, index) => {
							const isEmpty = index < Line;
							const isCP = IsCP && !(index < Line);
							return (
								<div
									className={betTeam.endsWith('s') ? 'bettype__ball-goal' : 'bettype__ball-miss'}
									data-status={isEmpty ? 'disabled' : isCP ? 'close-price' : chkSelected(betTeam, index) ? 'selected' : null}
									onClick={() => handleBallClick(choice.betTeam, index)}
									key={`${choice.betTeam}_${choice.label}`}
								>
									{choice.label}
								</div>
							);
						})
					}
				</Fragment>
			)
		})
	)
}